import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Link as LinkIcon,
  Description as DocumentIcon,
  VideoLibrary as VideoIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getDocumentList,
  getCategories,
  getSubCategories,
} from '../../endpoints/documents';
import { processLinkUrl } from '../../utils/urlUtils';
import { DocumentList } from '../../types/document';
import { applications } from '../../utils/variables';

const DocumentLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [delayedSearchTerm, setDelayedSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string>('');
  const [selectedSubCategories, setSelectedSubCategories] =
    useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Fetch categories and sub-categories
  const { data: categories = [] } = useApiGet(['categories'], getCategories);
  const { data: subCategories = [] } = useApiGet(
    ['sub-categories'],
    getSubCategories
  );

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDelayedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Fetch documents with filters
  const { data: documentsResponse, isLoading } = useApiGet(
    [
      'documents-library',
      delayedSearchTerm || '',
      selectedCategories || 'cat_none',
      selectedSubCategories || 'sub_none',
      currentPage,
      perPage,
    ],
    () =>
      getDocumentList({
        search: delayedSearchTerm || undefined,
        categories: selectedCategories ? [selectedCategories] : undefined,
        sub_categories: selectedSubCategories
          ? [selectedSubCategories]
          : undefined,
        page: currentPage,
        per_page: perPage,
      }),
    { enabled: true }
  );

  // Extract documents and pagination info from response
  const documents = documentsResponse?.data || [];
  const pagination = documentsResponse?.pagination;

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedCategories(value);

    // Clear sub-categories if category is changed
    setSelectedSubCategories('');
    // Reset to first page when changing filters
    setCurrentPage(1);
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedSubCategories(value);
    // Reset to first page when changing filters
    setCurrentPage(1);
  };

  const handleViewDocument = (document: DocumentList) => {
    // If user has edit permissions, go to DocumentEdit, otherwise to DocumentView
    if (canCreateDocument()) {
      navigate(`/library/documents/${document.id}/edit`);
    } else {
      // Use the document's version_id for the view route
      const versionId = document.version_id || 'current';
      navigate(`/library/documents/${document.id}/view/${versionId}`);
    }
  };

  const handleDownloadDocument = (id: string) => {
    // TODO: Implement download functionality
    console.log('Downloading document:', id);
  };

  const handleCopyLink = (id: string) => {
    // Find the document to get its version_id
    const document = documents.find((doc) => doc.id === id);
    if (document) {
      const versionId = document.version_id || 'current';
      const link = `${window.location.origin}/library/documents/${id}/view/${versionId}`;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          console.log('Link copied:', link);
        })
        .catch((err) => {
          console.error('Failed to copy link:', err);
          // Fallback: show the link in an alert
          alert(`Link: ${link}`);
        });
    }
  };

  const handleCreateDocument = () => {
    navigate('/library/documents/create');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDelayedSearchTerm('');
    setSelectedCategories('');
    setSelectedSubCategories('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Filter sub-categories based on selected categories
  const filteredSubCategories = subCategories.filter(
    (subCat) => selectedCategories === subCat.category_id
  );

  const getFileIcon = (document: DocumentList) => {
    if (document.video_url) {
      return <VideoIcon />;
    } else if (document.link_url) {
      return <LinkIcon />;
    } else if (document.downloadable) {
      return <DownloadIcon />;
    } else {
      return <DocumentIcon />;
    }
  };

  const canCreateDocument = () => {
    let apps = localStorage.getItem('apps');
    if (apps) {
      return apps.includes(applications.DocumentAdmin);
    }
    return false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Document Library
        </Typography>
        <Stack direction="row" spacing={1}>
          {canCreateDocument() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDocument}
              sx={{
                backgroundColor: '#00A859',
                '&:hover': { backgroundColor: '#008a4a' },
              }}
            >
              Create Document
            </Button>
          )}
          <Button
            variant={viewMode === 'grid' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
          >
            List
          </Button>
        </Stack>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search documents..."
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Categories</InputLabel>
                  <Select
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    input={<OutlinedInput label="Categories" />}
                    renderValue={(selected) => {
                      if (!selected) return 'Select a category';
                      const category = categories.find(
                        (cat) => cat.id === selected
                      );
                      return category?.name || selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>All Categories</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {filteredSubCategories.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sub-Categories</InputLabel>
                    <Select
                      value={selectedSubCategories}
                      onChange={handleSubCategoryChange}
                      input={<OutlinedInput label="Sub-Categories" />}
                      renderValue={(selected) => {
                        if (!selected) return 'Select a sub-category';
                        const subCategory = subCategories.find(
                          (subCat) => subCat.id === selected
                        );
                        return subCategory?.name || selected;
                      }}
                    >
                      <MenuItem value="">
                        <em>All Sub-Categories</em>
                      </MenuItem>
                      {filteredSubCategories.map((subCategory) => (
                        <MenuItem key={subCategory.id} value={subCategory.id}>
                          {subCategory.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {pagination
            ? `${pagination.total_count} document${pagination.total_count !== 1 ? 's' : ''} found`
            : `${documents.length} document${documents.length !== 1 ? 's' : ''} found`}
          {searchTerm && ` for "${searchTerm}"`}
          {selectedCategories &&
            ` in ${categories.find((cat) => cat.id === selectedCategories)?.name || selectedCategories}`}
          {pagination &&
            ` (showing ${(pagination.page - 1) * pagination.per_page + 1}-${Math.min(pagination.page * pagination.per_page, pagination.total_count)} of ${pagination.total_count})`}
        </Typography>
      </Box>

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Pagination
            count={pagination.total_pages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}

      {/* Documents Display */}
      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading documents...</Typography>
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No documents found
          </Typography>
          <Typography color="text.secondary">
            {searchTerm || selectedCategories || selectedSubCategories
              ? 'Try adjusting your search criteria or filters.'
              : 'No documents are available in the library.'}
          </Typography>
        </Paper>
      ) : viewMode === 'grid' ? (
        // Grid View
        <Grid container spacing={2}>
          {documents.map((document) => (
            <Grid item xs={12} md={6} lg={4} key={document.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 114, 206, 0.08)',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      },
                    }}
                    onClick={() => handleViewDocument(document)}
                  >
                    {getFileIcon(document)}
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ ml: 1, flexGrow: 1 }}
                    >
                      {document.name}
                    </Typography>
                  </Box>

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Version: <strong>{document.version}</strong>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Created:{' '}
                        <strong>
                          {new Date(document.created_at).toLocaleDateString()}
                        </strong>
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        flexGrow: 1,
                        color: canCreateDocument() ? '#0072CE' : 'inherit',
                        borderColor: canCreateDocument()
                          ? '#0072CE'
                          : 'inherit',
                        '&:hover': {
                          borderColor: canCreateDocument()
                            ? '#005a9e'
                            : 'inherit',
                          backgroundColor: canCreateDocument()
                            ? 'rgba(0, 114, 206, 0.04)'
                            : 'inherit',
                        },
                      }}
                      startIcon={
                        canCreateDocument() ? <EditIcon /> : <ViewIcon />
                      }
                      onClick={() => handleViewDocument(document)}
                    >
                      {canCreateDocument() ? 'Edit' : 'View'}
                    </Button>
                    {document.downloadable && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDocument(document.id)}
                        sx={{
                          flexGrow: 1,
                          backgroundColor: '#00A859',
                          '&:hover': {
                            backgroundColor: '#008A4A',
                          },
                        }}
                      >
                        Download
                      </Button>
                    )}
                    {document.link_url && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        href={processLinkUrl(document.link_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          flexGrow: 1,
                          color: '#FF6B35',
                          borderColor: '#FF6B35',
                          '&:hover': {
                            borderColor: '#E55A2B',
                            backgroundColor: 'rgba(255, 107, 53, 0.04)',
                          },
                        }}
                      >
                        Go to
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // List View
        <Card>
          <List>
            {documents.map((document, index) => (
              <ListItem
                key={document.id}
                divider={index < documents.length - 1}
                sx={{ py: 2 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 114, 206, 0.08)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    },
                  }}
                  onClick={() => handleViewDocument(document)}
                >
                  <ListItemIcon>{getFileIcon(document)}</ListItemIcon>
                  <ListItemText
                    primary={document.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" component="span">
                          Version {document.version} •{' '}
                          {new Date(document.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={canCreateDocument() ? 'Edit' : 'View'}>
                    <IconButton
                      size="small"
                      sx={{
                        color: canCreateDocument() ? '#0072CE' : 'inherit',
                      }}
                      onClick={() => handleViewDocument(document)}
                    >
                      {canCreateDocument() ? <EditIcon /> : <ViewIcon />}
                    </IconButton>
                  </Tooltip>
                  {document.downloadable && (
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        sx={{ color: '#00A859' }}
                        onClick={() => handleDownloadDocument(document.id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {document.link_url && (
                    <Tooltip title="Go to">
                      <IconButton
                        size="small"
                        sx={{ color: '#FF6B35' }}
                        href={processLinkUrl(document.link_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Copy Link">
                    <IconButton
                      size="small"
                      sx={{ color: '#6C63FF' }}
                      onClick={() => handleCopyLink(document.id)}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        </Card>
      )}

      {/* Bottom Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={pagination.total_pages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default DocumentLibrary;
